/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Shared Authentication Utilities
 * 
 * Common authentication helper functions used across client and server.
 * Includes mock user creation for development and testing purposes.
 */

import type { User } from '@shared/types/user';
import { USER_ROLES, type UserRole } from '@shared/schema';

/**
 * Create a mock admin user for development/testing
 * Used by server middleware and main client AuthContext when DISABLE_AUTH=true
 * 
 * Note: This uses hardcoded defaults for the mock user since it's called from
 * client-side code where config module may not be available. The actual Default Admin
 * user created by the server uses the centralized config system.
 */
export function createMockAdminUser(): User {
  // Hardcoded defaults for mock user - matches DEFAULT_ORG_ID env var default
  const DEFAULT_ORG_ID = 'd4ba32f1-7cd0-4b39-acfd-ef89a5e84a9c';
  
  return {
    id: 1,
    uuid: '00000000-0000-0000-0000-000000000001', // Mock UUID for dev user
    organizationId: DEFAULT_ORG_ID, // Default SkillShop Foundation organization
    name: 'Development User',
    email: 'dev@localhost',
    role: USER_ROLES.ADMIN,
    photoUrl: null,
    bio: 'Mock admin user for development with disabled authentication',
    emailVerified: true,
    isActive: true,
    lastLoginAt: new Date(),
    createdAt: new Date(),
    updatedAt: new Date(),
    authProvider: 'local',
    googleId: null,
    microsoftId: null,
    firebaseUid: null,
    workosUserId: null,
    masterUserId: null,
    lastSyncedAt: null
  };
}

/**
 * Create a mock learner user for development/testing
 * Used by learning client AuthContext in development mode
 */
export function createMockLearnerUser(): User {
  // Hardcoded defaults for mock user - matches DEFAULT_ORG_ID env var default
  const DEFAULT_ORG_ID = 'd4ba32f1-7cd0-4b39-acfd-ef89a5e84a9c';
  
  return {
    id: 3, // Alex Rivera from the example data
    uuid: '00000000-0000-0000-0000-000000000003', // Mock UUID for learner
    organizationId: DEFAULT_ORG_ID, // Default SkillShop Foundation organization
    name: 'Alex Rivera',
    email: 'learner@skillshop.com',
    role: USER_ROLES.LEARNER,
    photoUrl: null,
    bio: 'Aspiring full-stack developer looking to improve skills in web development, design, and data science.',
    emailVerified: true,
    isActive: true,
    lastLoginAt: new Date(),
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date(),
    authProvider: 'local',
    googleId: null,
    microsoftId: null,
    firebaseUid: null,
    workosUserId: null,
    masterUserId: null,
    lastSyncedAt: null
  };
}

